<script setup>
  import { computed } from 'vue';
  import { useSpotStore } from "@/stores/SpotStore";
  import useSpotGlobals from '@/composables/useSpotGlobals';
  import Icon from "@/components/icons/Icon.vue";

  const { $ALLOWED_STATION_CAPABILITIES } = useSpotGlobals();

  const props = defineProps({
    upgrade: {
      type: Array,
      required: true
    },
    allowedDataValues: {
      type: Array,
      default: () => []
    },
    tagList: {
      type: Boolean,
      default: false
    }
  });

  const spotStore = useSpotStore();

  // Also used for display order
  const defaultAllowedDataValues = [
    'windGust',
    'airTemp',
    'surfaceTemp',
    'relativeHumidity',
    'airPressure',
    'waterTemp',
    'waterSkinTemp',
    'swell',
    'waterLevel',
    'stationElevation',
  ];

  const allowedDataValues = computed(() => {
    const dataValues = props.allowedDataValues.length ? props.allowedDataValues : defaultAllowedDataValues;

    if (spotStore.isNowcast) {
      const windGustIndex = dataValues.indexOf('windGust');

      if (windGustIndex > -1) {
        dataValues.splice(windGustIndex, 1);
      }
    }   

    return dataValues;
  });

  const currentConditionsDisplayData = computed(() => {
    const displayData = [];

    if (isAllowed('windGust')) displayData[getDataPosition('windGust')] = ['Gusting', getGustingDisplayString(spotStore.formattedData.windGust, spotStore.stationApiDataValues.lull), 'gusts'];
    if (isAllowed('airTemp')) displayData[getDataPosition('airTemp')] = ['Air Temp', spotStore.formattedData.airTemp, 'thermometer'];
    if (isAllowed('airPressure')) displayData[getDataPosition('airPressure')] = ['Air Pressure', spotStore.formattedData.airPressure, 'pressure-heavy'];

    if (isAllowed('surfacetemp') && $ALLOWED_STATION_CAPABILITIES['surfacetemp'] && spotStore.stationApiDataValues.surface_temp) {
      displayData[getDataPosition('surfaceTemp')] = ['Surface Temp', spotStore.formattedData.surfaceTemp, 'surface-temp'];
    }

    if (isAllowed('relativeHumidity') && spotStore.stationApiDataValues.humidity) {
      displayData[getDataPosition('relativeHumidity')] = ['Rel Humidity', spotStore.formattedData.relativeHumidity, 'humidity-heavy'];
    }    
    
    if (isAllowed('waterTemp') && spotStore.stationApiDataValues.wtemp) {
      displayData[getDataPosition('waterTemp')] = ['Water Temp', spotStore.formattedData.waterTemp, 'sst'];
    }
    
    if (isAllowed('swell') && spotStore.stationApiDataValues.wave_height && spotStore.stationApiDataValues.wave_height > 0) {
      const formattedWaveHeightData = spotStore.formattedData.waveHeight;
      const swellFormattedData = spotStore.stationApiDataValues.wave_period ? `${formattedWaveHeightData} @ ${spotStore.stationApiDataValues.wave_period} s` : formattedWaveHeightData;

      displayData[getDataPosition('swell')] = ['Swell', swellFormattedData, 'tides'];
    }

    if (isAllowed('waterLevel') && spotStore.defaultStation.water_level && spotStore.stationApiDataValues.water_level > 0) {
      if (spotStore.spotId === 165021 || spotStore.spotId === 165020) {
        displayData[getDataPosition('waterLevel')] = ['Water Level', 'N/A', 'water-level']
        
        return;
      };

      displayData[getDataPosition('waterLevel')] = ['Water Level', spotStore.formattedData.waterLevel, 'water-level'];
    }

    if (isAllowed('stationElevation') && spotStore.defaultStation.station_elevation && spotStore.stationApiDataValues.station_elevation > 0) {
      displayData[getDataPosition('stationElevation')] = ['Height Above Ground', spotStore.formattedData.stationElevation, 'info'];
    }
    
    // Remove empty array indexes and return;
    return displayData.filter(data => data);
  });

  function getGustingDisplayString(formattedGustValue, lullValue) {
    if (!spotStore.stationApiDataValues.gust) return formattedGustValue;

    if (spotStore.status === 4) return '<span class="jwx-data-value__value">&ndash;&ndash;</span>'

    const lullString = lullValue || '';

    return `${lullString} to ${formattedGustValue}`;
  }

  function getDataPosition(dataName) {
    return allowedDataValues.value.indexOf(dataName);
  }  

  function isAllowed(dataName) {
    return allowedDataValues.value.includes(dataName);
  }
</script>

<template>
  <ul
    class="jwx-v-current-conditons-data-list jwx-data-list"
    :class="{'jwx-data-list--tags': props.tagList}"
    :data-tag-count="currentConditionsDisplayData.length"
  >
    <li 
      v-for="[label, value, icon] in currentConditionsDisplayData" 
      :key="label" 
      class="jwx-v-current-conditons-data-list__item jwx-data-list__line-item"
      :data-icons="!props.tagList ? 'true' : null"
    >
      <Icon :name="icon" class="jwx-data-list__icon"/>
      <p class="jwx-data-list__label">{{ label }}</p>

      <p
        v-if="label.toLowerCase() === 'gusting' && props.upgrade[0]"
        class="jwx-data-list__value"
      >
        <Icon name="lock" class="jwx-data-list__upgrade-icon"/>
      </p>

      <div v-else v-html="value" class="jwx-data-list__value"></div>
    </li>
  </ul>
</template>

<style>
  .jwx-v-current-conditons-data-list {
    grid-area: data-list;
  }

  @media screen and (max-width: 549px) {
    .jwx-data-list:not(.jwx-data-list--scroller, .jwx-data-list--blocks, .jwx-data-list--tags) {
      padding: var(--jwx-block-spacing-xxs) var(--jwx-block-spacing);
      background-color: var(--jwx-surface);
      border-radius: 8px;
    }
  }

  .jwx-data-list:not(.jwx-data-list--scroller, .jwx-data-list--blocks, .jwx-data-list--tags) .jwx-v-current-conditons-data-list__item:first-of-type {
    padding-top: 0;
  }

  .jwx-data-list:not(.jwx-data-list--scroller, .jwx-data-list--blocks, .jwx-data-list--tags) .jwx-v-current-conditons-data-list__item:last-of-type {
    padding-bottom: 0;
  }
</style>